import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MOVIES_INITIAL_STATE, MoviesState } from './movies.model';
import { withCallState } from '@shared/store-features/call-state-feature';
import { CallStateEnum } from '@shared/models/call-state.model';
import { SnackbarType } from '@shared/models/app-snackbar.model';
import useSnackbarStore from '@store/snackbar-store/snackbar.store';
import {
  createMovies,
  deleteMovie,
  deleteMoviesMany,
  editMoviesRow
} from '@shared/api/entertainment-api/movies-api';
import axios from 'axios';
import {
  createGenre,
  deleteGenre,
  editGenre,
  getGenres
} from '@api/entertainment-api/entertainment-api.ts';
import { GenresEnum } from '@modules/entertainment/models/entertainment.models.ts';

const { showSnackbar } = useSnackbarStore.getState();

const useMoviesStore = create<MoviesState>()(
  devtools(
    (set, getState) => ({
      ...MOVIES_INITIAL_STATE,
      ...withCallState(getState),

      getGenreList: async () => {
        try {
          const genreData = await getGenres(GenresEnum.Movies);

          const genreList = genreData.map((genre) => ({
            id: genre?._id,
            name: genre.name,
            image: genre.image
          }));

          set({
            genreList
          });
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createRow: async ({ data, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await createMovies(data);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_success_movie_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },
      editRow: async ({ data, state, handleClose, id }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await editMoviesRow(data, id);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_update_movie_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      deleteRow: async ({ id, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          if (id instanceof Array) {
            await deleteMoviesMany(id);
          } else {
            await deleteMovie(id);
          }
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_delete_movie_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createGenre: async ({ data, handleClose }) => {
        set({ genreCallState: CallStateEnum.Loading });
        try {
          await createGenre(data);
          await getState().getGenreList();
          set({ genreCallState: CallStateEnum.Loaded });
          handleClose?.();
        } catch (error) {
          set({ genreCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }

          throw error;
        }
      },

      editGenre: async ({ data, id, handleClose }) => {
        set({ genreCallState: CallStateEnum.Loading });
        try {
          await editGenre(data as any, id);
          await getState().getGenreList();
          set({ genreCallState: CallStateEnum.Loaded });
          handleClose?.();
        } catch (error) {
          set({ genreCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      deleteGenre: async ({ id, handleClose }) => {
        set({ genreCallState: CallStateEnum.Loading });
        try {
          await deleteGenre(id);
          await getState().getGenreList();
          set({ genreCallState: CallStateEnum.Loaded });
          handleClose?.();
        } catch (error) {
          set({ genreCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      isGenreLoading: () => getState().genreCallState === CallStateEnum.Loading,
      isGenreLoaded: () => getState().genreCallState === CallStateEnum.Loaded,
      isGenreError: () => getState().genreCallState === CallStateEnum.Error
    }),

    { name: 'MoviesStore' }
  )
);

export default useMoviesStore;
